import {
  clone,
  every,
  forEach,
  groupBy,
  last,
  map,
  mapValues,
  range,
  some,
  sortBy,
  times,
} from 'lodash';

import nullthrows from 'nullthrows';
import * as CardAbilities from './CardAbilities';
import { AGES_PER_GAME } from './GameConstants';
import { EventTypes, GameEvent } from './GameEvents';
import { GamePlayerComputedState } from './GameModel';
import { GameViewModel } from './GameViewModel';
import { getPhaseOrder, Phase } from './Phases';
import { probabilityWinningMultiBattle } from './Probability';
import * as Rules from './Rules';
import { CardWithID } from './Rules';
import * as Utility from './Utility';
import { applyCounterDelta, makeCounterDelta, sumCounters } from './Utility';

// var total_turns(game) = game.turnsPerAge * AGES_PER_GAME;
function total_turns(game: GameViewModel) {
  return game.turnsPerAge * AGES_PER_GAME;
}

export type AugmentedGameEvent = GameEvent & {
  age: number;
  turn: number;
  phase: Phase;
};

export type MilitaryStats = {
  text: string;
  winningPlayerID: string | null; // null when No Dice+ mode results in a tie
  playerIDToProbability: { [k: string]: number };
  playerIDToBonus: { [k: string]: number };
  age: number;
  turn: number;
  cardID: string;
};

export function augmentGameEvents(game: GameViewModel): AugmentedGameEvent[] {
  let age = 0;
  let turn = 0;
  let phase: Phase = Phase.SETUP;
  return game.events.map((event) => {
    if (event.type === EventTypes.SET_UP_AGE) {
      age = event.payload.age;
    } else if (event.type === EventTypes.SET_UP_TURN) {
      turn = event.payload.turn;
    } else if (event.type === EventTypes.SET_UP_PHASE) {
      phase = event.payload.phase;
    }
    return {
      ...event,
      age,
      turn,
      phase,
    };
  });
}

var Stats = {
  computeMilitaryStats: (game: GameViewModel, events: AugmentedGameEvent[]) => {
    const allPlayerIDs = game.playerStates.map((player) => player.userID);

    const military_items = events.filter((item) => {
      return item.type === EventTypes.CONFLICT_RESULTS;
    });
    const grouped_items = groupBy(military_items, (item) => {
      return item.turn + (item.age - 1) * game.turnsPerAge;
    });

    const results = times(total_turns(game), (i) => {
      const outcome_by_player = Object.fromEntries(
        allPlayerIDs.map((id) => [id, null as MilitaryStats | null]),
      );

      const items = grouped_items[i + 1] || [];
      items.forEach((item) => {
        const first_round = item.payload.rounds![0];
        const playerIDs = Object.keys(first_round);
        const winningPlayerID = item.payload.winningPlayerID ?? null;
        const age = item.age;
        const playerIDToBonus = Object.fromEntries(
          playerIDs.map((id) => [id, first_round[id].bonus]),
        );
        const playerIDToProbability = Object.fromEntries(
          playerIDs.map((id) => [
            id,
            probabilityWinningMultiBattle(
              age,
              playerIDToBonus[id],
              playerIDs
                .filter((x) => x !== id)
                .map((x) => [age, playerIDToBonus[x]]),
            ),
          ]),
        );

        // Fill in data for participating players
        const isContested = playerIDs.length > 1;
        forEach(playerIDToBonus, (bonus, id) => {
          let text = '';
          if (isContested) {
            let modifier = '';
            if (
              every(playerIDToBonus, (b, i) => {
                return id === i || bonus > b;
              })
            ) {
              modifier = '+';
            } else if (
              some(playerIDToBonus, (b, i) => {
                if (id === i) {
                  return false;
                }
                return bonus < b;
              })
            ) {
              modifier = '-';
            }
            // In No Dice+ mode, winningPlayerID can be null (tie = all lose)
            const outcome =
              winningPlayerID === null
                ? 'T'
                : id === winningPlayerID
                  ? 'W'
                  : 'L';
            text = outcome + modifier;
          }
          outcome_by_player[id] = {
            cardID: item.payload.cardID!,
            winningPlayerID,
            text,
            playerIDToProbability,
            playerIDToBonus,
            age,
            turn: (i % game.turnsPerAge) + 1,
          };
        });
      });

      return outcome_by_player;
    });

    return results;
  },
  computeGainedCard: (game: GameViewModel, events: AugmentedGameEvent[]) => {
    var items = events.filter((item) => {
      return item.type === EventTypes.GAIN_CARD;
    });

    var grouped_items = groupBy(items, (item) => {
      return item.turn + Math.max(item.age - 1, 0) * game.turnsPerAge;
    });

    var cardsByID = game.cardsByID;

    return times(total_turns(game) + 1, (i) => {
      var turn_items = grouped_items[i] || [];

      var cardByPlayerID: { [k: string]: CardWithID } = {};
      turn_items.forEach((item) => {
        var payload = item.payload;
        var card = cardsByID[payload.cardID];
        cardByPlayerID[payload.userID] = card;
      });

      return cardByPlayerID;
    });
  },
  computeGainedTributes: (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) => {
    const items = events.filter((item) => {
      return item.type === EventTypes.GAIN_TRIBUTES;
    });

    const grouped_items = groupBy(items, (item) => {
      return item.turn + Math.max(item.age - 1, 0) * game.turnsPerAge;
    });

    const playerIDs = game.playerStates.map((player) => player.userID);
    return times(total_turns(game), (i) => {
      const turn_items = grouped_items[i + 1] || [];

      const tributesByPlayerID = Object.fromEntries(
        playerIDs.map((id) => [id, [] as string[]]),
      );
      turn_items.forEach((item) => {
        const payload = item.payload;
        tributesByPlayerID[payload.userID] = payload.tributeIDs;
      });

      return tributesByPlayerID;
    });
  },
  computeGainedFavorStats: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    var items = events.filter((item) => {
      return (
        item.type === EventTypes.PRODUCTION ||
        item.type === EventTypes.GAIN_CARD ||
        item.type === EventTypes.GAIN_TRIBUTES ||
        // item.type === EventTypes.END_GAME_SCORING ||
        item.type === EventTypes.EARN_TOKEN ||
        item.type === EventTypes.END_GAME_EFFECTS
      );
    });

    var grouped_items = groupBy(items, (item) => {
      return Math.max(0, item.turn + (item.age - 1) * game.turnsPerAge);
    });

    var player_ids = game.playerStates.map((player) => {
      return player.userID;
    });
    var totals_by_player = Object.fromEntries(
      player_ids.map((id) => [
        id,
        {
          total_card_favor: 0,
          total_triggered_favor: 0,
          total_tribute_favor: 0,
          total_prophecy_favor: 0,
        },
      ]),
    );

    // used to rebuild the player cards turn by turn
    const players_reconstructed = game.playerStates.map((player) => {
      return {
        ...player,
        cards: [] as CardWithID[],
      };
    });

    return range(total_turns(game) + 1).map((i) => {
      var turn_items = grouped_items[i] || [];

      var stats_by_player = mapValues(totals_by_player, clone);
      turn_items.forEach((item) => {
        var playerID = item.payload.userID!;
        var favor = 'favor' in item.payload ? item.payload.favor : 0;

        var stats: any = stats_by_player[playerID];
        if (item.type === EventTypes.GAIN_CARD) {
          // Use the card's base favor from definition, not cardCounters which includes prophecy
          const card = game.cardsByID[item.payload.cardID] as CardWithID;
          const baseFavor = card?.favor ?? 0;
          stats.delta_card_favor = baseFavor;
          stats.total_card_favor += baseFavor;

          const player_reconstructed = players_reconstructed.find(
            (p) => p.userID === playerID,
          );
          if (player_reconstructed) {
            player_reconstructed.cards.push(card);
            const context = Rules.makeContextFromGame(
              player_reconstructed,
              game,
            );
            stats.total_prophecy_favor =
              Rules.computeEndOfGameEffectsFavor(context).favor;
          }
          // } else if (item.type === EventTypes2.EARN_TOKEN) {
          //   var triggered_favor = item.payload.triggeredCards?.favor || 0;
          //   stats.delta_triggered_favor = triggered_favor;
          //   stats.total_triggered_favor += triggered_favor;
        } else if (item.type === EventTypes.GAIN_TRIBUTES) {
          var triggered_favor = item.payload.triggeredCards?.reduce(
            (acc, card) => {
              return applyCounterDelta(acc, card.counters);
            },
            makeCounterDelta(),
          ).favor;
          favor! += triggered_favor!;
          stats.delta_tribute_favor = favor;
          stats.total_tribute_favor += favor;
          stats.delta_triggered_favor = triggered_favor;
          stats.total_triggered_favor += triggered_favor;
        }

        var totals = totals_by_player[playerID];
        totals.total_card_favor = stats.total_card_favor;
        totals.total_triggered_favor = stats.total_triggered_favor;
        totals.total_tribute_favor = stats.total_tribute_favor;
        totals.total_prophecy_favor = stats.total_prophecy_favor;
      });
      return stats_by_player;
    });
  },

  // returns an array of turns, each turn  is an object of the form:
  // { phase1: phase1_time_by_player, ..., phaseN: phaseN_time_by_player }
  getTimingDataByTurn: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    var phase_end_events = events.filter((event) => {
      return event.type === EventTypes.END_OF_PHASE;
    });
    var phase_end_items = phase_end_events.map((event) => {
      return {
        age: event.age,
        turn: event.turn,
        phase: event.phase,
        times: event.payload.elapsedPhaseMillisByPlayerID,
      };
    });
    var data_by_turn = groupBy(phase_end_items, (event) => {
      return (event.age - 1) * game.turnsPerAge + event.turn;
    });

    let zero_time_by_player = Object.fromEntries(
      game.playerStates.map((player) => {
        return [player.userID, 0];
      }),
    );

    return times(total_turns(game) + 1, (i) => {
      let turn_data = data_by_turn[i];
      return Object.fromEntries(
        getPhaseOrder().map((phase) => {
          let phase_data = turn_data?.find((event) => event.phase === phase);
          return [
            phase,
            (phase_data && phase_data.times) || clone(zero_time_by_player),
          ];
        }),
      );
    });
  },

  // returns a map of player to total time in ms
  computeTotalTimeByPlayer: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    return sum_timing_data_by_player(Stats.getTimingDataByTurn(game, events));
  },

  // applies operator to the timing data generated by getTimingDataByTurn
  computeTimingEvents: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
    operator: (phase_times: { [userid: string]: number }) => {
      [userid: string]: number;
    },
  ) {
    var timing_data = Stats.getTimingDataByTurn(game, events);
    return timing_data.map((turn) => {
      return mapValues(turn, operator);
    });
  },

  // transforms timing data from getTimingDataByTurn to blame time
  computeBlameTimingDataByTurn: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    return Stats.computeTimingEvents(game, events, (phase_times) => {
      var sorted_times = sort_phase_timing_data(phase_times);
      var last_time = 0;
      var num_blame_players = game.playerStates.length;
      var total_blame_time = 0;
      var blame_times = sorted_times.map((time_data) => {
        var current_time = time_data.time;
        total_blame_time += (current_time - last_time) / num_blame_players;
        last_time = current_time;
        --num_blame_players;
        return [time_data.user_id, total_blame_time];
      });
      return Object.fromEntries(blame_times);
    });
  },
  computeTotalBlameTimeByPlayer: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    return sum_timing_data_by_player(
      Stats.computeBlameTimingDataByTurn(game, events),
    );
  },

  // transforms timing data from getTimingDataByTurn to solo thinking time
  computeSoloThinkingTimingDataByTurn: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    return Stats.computeTimingEvents(game, events, (phase_times) => {
      var sorted_times = sort_phase_timing_data(phase_times);
      var solo_times = sorted_times.map((time_data) => {
        var user_id = time_data.user_id;
        var time = time_data.time;
        if (game.playerStates.length === 1) {
          return [user_id, time];
        }
        var longest_time = last(sorted_times)!.time;
        if (longest_time !== time) return [user_id, 0];
        var second_longest_time =
          sorted_times[game.playerStates.length - 2].time;
        return [user_id, longest_time - (second_longest_time || 0)];
      });
      return Object.fromEntries(solo_times);
    });
  },
  computeTotalSoloThinkingTimeByPlayer: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    return sum_timing_data_by_player(
      Stats.computeSoloThinkingTimingDataByTurn(game, events),
    );
  },

  computeBidData: function (game: GameViewModel, events: AugmentedGameEvent[]) {
    const items = events.filter((item) => {
      return item.type === EventTypes.BID;
    });
    const grouped_items = groupBy(items, (item) => {
      return item.turn + Math.max(item.age - 1, 0) * game.turnsPerAge;
    });

    return times(total_turns(game) + 1, (i) => {
      if (i === 0) {
        return Object.fromEntries(
          game.playerStates.map((player) => [player.userID, 0]),
        );
      }
      const turn_items = nullthrows(grouped_items[i]);
      return Object.fromEntries(
        turn_items.map((item) => {
          return [item.payload.userID, item.payload.spentMilitary];
        }),
      );
    });
  },

  computeProductionData: function (
    game: GameViewModel,
    events: AugmentedGameEvent[],
  ) {
    let items = events.filter((item) => {
      return (
        item.type === EventTypes.PRODUCTION ||
        item.type === EventTypes.PURCHASE_CARD ||
        item.type === EventTypes.GAIN_CARD ||
        item.type === EventTypes.BID ||
        item.type === EventTypes.GAIN_TRIBUTES ||
        item.type === EventTypes.EARN_TOKEN
      );
    });

    let grouped_items = groupBy(items, (item) => {
      return Math.max(0, item.turn + (item.age - 1) * game.turnsPerAge);
    });

    let playerByID: { [k: string]: GamePlayerComputedState } =
      Object.fromEntries(
        game.playerStates.map((serialized_player) => {
          return [serialized_player.userID, serialized_player];
        }),
      );

    let counters_by_player = mapValues(playerByID, (player, id) => {
      return {
        gold: 0,
        military: 0,
        gold_income: 0,
        military_income: 0,
        gold_base: 0,
        military_base: 0,
      };
    });

    let results = times(total_turns(game) + 1, (i) => {
      let index = i;

      let items = grouped_items[index] || [];

      // saves a snapshot of player tokens at the production step
      let production_step_resources_by_player = mapValues(
        playerByID,
        (player, id) => {
          return {
            gold: counters_by_player[id].gold,
            military: counters_by_player[id].military,
          };
        },
      );
      items.forEach((item) => {
        let player_id = item.payload.userID;

        if (item.type === EventTypes.PRODUCTION) {
          // this cap math could be incorrect if order of events is wrong
          // e.g. if gold is subtracted from a GAIN_CARD event before the production event
          counters_by_player[player_id].gold =
            counters_by_player[player_id].gold +
            (item.payload.counters.gold ?? 0);
          counters_by_player[player_id].military =
            counters_by_player[player_id].military +
            (item.payload.counters.military ?? 0);
          production_step_resources_by_player[player_id].gold =
            counters_by_player[player_id].gold;
          production_step_resources_by_player[player_id].military =
            counters_by_player[player_id].military;
          counters_by_player[player_id].gold_income =
            item.payload.counters.gold ?? 0;
          counters_by_player[player_id].military_income =
            item.payload.counters.military ?? 0;
        } else if (item.type === EventTypes.PURCHASE_CARD) {
          let gold_spent = item.payload.spentCounters?.gold ?? 0;
          counters_by_player[player_id].gold -= gold_spent;
        } else if (item.type === EventTypes.GAIN_CARD) {
          let card = game.cardsByID[item.payload.cardID!!] as CardWithID;
          if (card) {
            // currently looks at end-game snapshot, which would be inaccurate if any
            // scaling base values are added in the future
            const context = Rules.makeContextFromGame(
              playerByID[player_id],
              game,
            );
            const baseAbility = CardAbilities.getBaseCountersAbility(card);
            if (baseAbility) {
              let delta = Utility.makeCounterDelta();
              Utility.applyCounterDelta(
                delta,
                baseAbility.getBaseCounters(context),
              );
              counters_by_player[player_id].gold_base += delta.gold;
              counters_by_player[player_id].military_base += delta.military;
            }
          }
          counters_by_player[player_id].gold += item.payload.counters.gold ?? 0;
          counters_by_player[player_id].military +=
            item.payload.counters.military ?? 0;
        } else if (item.type === EventTypes.BID) {
          let military_spent = item.payload.spentMilitary ?? 0;
          counters_by_player[player_id].military -= military_spent;
        } else if (item.type === EventTypes.GAIN_TRIBUTES) {
          const totalDelta = sumCounters(
            item.payload.triggeredCards.map((card) => card.counters),
          );
          counters_by_player[player_id].gold += totalDelta.gold;
          counters_by_player[player_id].military += totalDelta.military;
        } else if (item.type === EventTypes.EARN_TOKEN) {
          const totalDelta = sumCounters(
            item.payload.triggeredCards.map((card) => card.counters),
          );
          counters_by_player[player_id].gold += totalDelta.gold;
          counters_by_player[player_id].military += totalDelta.military;
        }
      });

      let ret = mapValues(playerByID, (player, id) => {
        return {
          gold:
            production_step_resources_by_player[id].gold +
            counters_by_player[id].gold_base,
          military:
            production_step_resources_by_player[id].military +
            counters_by_player[id].military_base,
          gold_income: counters_by_player[id].gold_income,
          military_income: counters_by_player[id].military_income,
          gold_base: counters_by_player[id].gold_base,
          military_base: counters_by_player[id].military_base,
        };
      });
      return ret;
    });

    return results;
  },

  getWinningFavor: function (game: GameViewModel) {
    let highest_favor = Math.max(
      ...game.playerStates.map((player) => player.counters.favor),
    );
    return highest_favor;
  },
  getWinningUserIDs: function (game: GameViewModel) {
    let winning_favor = Stats.getWinningFavor(game);
    return game.playerStates
      .filter((player) => {
        return player.counters.favor === winning_favor;
      })
      .map((player) => player.userID);
  },
};

// takes in timing data (or modified timing data) and returns the total time for each player
function sum_timing_data_by_player(timing_data: any) {
  const time_by_player: { [userid: string]: number } = {};
  timing_data.forEach((turn: any) => {
    forEach(turn, (phase_times: any) => {
      forEach(phase_times, (time: any, user_id: string) => {
        time_by_player[user_id] = (time_by_player[user_id] || 0) + time;
      });
    });
  });
  return time_by_player;
}

// takes in phase timing data of the form {user1: time1, ..., userN: timeN}
// and returns it in a sorted order of the form [..., {user_id: user1, time: time1}, ...]
function sort_phase_timing_data(phase_times: {
  [userid: string]: number;
}): { user_id: string; time: number }[] {
  return sortBy(
    map(phase_times, (time, user_id) => {
      return { user_id, time };
    }),
    (timing_data) => {
      return timing_data.time;
    },
  );
}

export default Stats;
